import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3877">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3878">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3879">
          <ArrowLeftIcon size={20} data-id="element-3880" />
          {t('pages.BillFinancingWizard.back_to_bill')}
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3881">
        <div className="flex items-center justify-between mb-8" data-id="element-3882">
          <div className="flex-1" data-id="element-3883">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3884">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3885">
                1
              </div>
              <div className="ml-3" data-id="element-3886">
                <div className="text-sm font-medium" data-id="element-3887">
                  {t('pages.BillApprovalWizard.bill_details')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3888"></div>
          <div className="flex-1" data-id="element-3889">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3890">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3891">
                2
              </div>
              <div className="ml-3" data-id="element-3892">
                <div className="text-sm font-medium" data-id="element-3893">
                  {t('pages.BillFinancingWizard.select_provider')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3894"></div>
          <div className="flex-1" data-id="element-3895">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3896">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3897">
                3
              </div>
              <div className="ml-3" data-id="element-3898">
                <div className="text-sm font-medium" data-id="element-3899">
                  {t('pages.BillFinancingWizard.financing_terms')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3900"></div>
          <div className="flex-1" data-id="element-3901">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3902">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3903">
                4
              </div>
              <div className="ml-3" data-id="element-3904">
                <div className="text-sm font-medium" data-id="element-3905">
                  {t('pages.BillFinancingWizard.review_submit')}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-3906">
            <div data-id="element-3907">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3908">
                {t('pages.BillFinancingWizard.bill_information')}
              </h2>
              <p className="text-gray-600" data-id="element-3909">
                {t('pages.BillFinancingWizard.review_the_bill_details_for_financing')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3910">
              <div className="grid grid-cols-2 gap-4" data-id="element-3911">
                <div data-id="element-3912">
                  <div className="text-sm text-gray-500" data-id="element-3913">
                    {t('pages.BillFinancingWizard.bill_number')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3914">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-3915">
                  <div className="text-sm text-gray-500" data-id="element-3916">
                    {t('pages.BillFinancingWizard.supplier')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3917">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-3918">
                  <div className="text-sm text-gray-500" data-id="element-3919">
                    {t('pages.BillDetail.due_date')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3920">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-3921">
                  <div className="text-sm text-gray-500" data-id="element-3922">
                    {t('pages.BillFinancingWizard.bill_amount')}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-3923">
                    {bill.currency} {bill.amount.toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3924">
              <div className="flex items-start gap-3" data-id="element-3925">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3926" />
                <div className="text-sm text-blue-800" data-id="element-3927">
                  <div className="font-medium mb-1" data-id="element-3928">
                    {t('pages.BillFinancingWizard.bill_financing_benefits')}
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-3929">
                    <li data-id="element-3930">
                      {t('pages.BillFinancingWizard.improve_cash_flow_by_paying_suppliers')}
                    </li>
                    <li data-id="element-3931">
                      {t('pages.BillFinancingWizard.maintain_good_supplier_relationships')}
                    </li>
                    <li data-id="element-3932">
                      {t('pages.BillFinancingWizard.take_advantage_of_early_payment_discounts')}
                    </li>
                    <li data-id="element-3933">
                      {t('pages.BillFinancingWizard.flexible_repayment_terms')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-3934">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3935">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-3936">
            <div data-id="element-3937">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3938">
                {t('pages.BillFinancingWizard.select_pomelo_financing_package')}
              </h2>
              <p className="text-gray-600" data-id="element-3939">
                {t('pages.BillFinancingWizard.choose_a_financing_package_that_best')}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-3940">
              <div className="flex items-start gap-3" data-id="element-3941">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3942" />
                <div data-id="element-3943">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-3944">
                    {t('pages.BillFinancingWizard.pomelo_your_trusted_financial_partner')}
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-3945">
                    {t('pages.BillFinancingWizard.as_a_valued_pomelo_customer_you')}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-3946">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-3947">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-3948" />

                  <div className="flex items-start justify-between" data-id="element-3949">
                    <div className="flex-1" data-id="element-3950">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3951">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-3952">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-3953" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-3954">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3955">
                        <div data-id="element-3956">
                          <div className="text-gray-500" data-id="element-3957">
                            {t('pages.BillFinancingWizard.interest_rate')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3958">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-3959">
                          <div className="text-gray-500" data-id="element-3960">
                            {t('pages.BillFinancingWizard.max_term')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3961">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-3962">
                          <div className="text-gray-500" data-id="element-3963">
                            {t('pages.BillFinancingWizard.processing_time')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3964">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-3965">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3966">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3967">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-3968">
            <div data-id="element-3969">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3970">
                {t('pages.BillFinancingWizard.financing_terms')}
              </h2>
              <p className="text-gray-600" data-id="element-3971">
                {t('pages.BillFinancingWizard.specify_the_financing_amount_and_terms')}
              </p>
            </div>
            <div className="space-y-6" data-id="element-3972">
              <div data-id="element-3973">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3974">
                  {t('pages.BillFinancingWizard.financing_amount_usd')}
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder={t('pages.BillFinancingWizard.attrs.enter_amount')} max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3975" />

                <div className="mt-1 text-sm text-gray-500" data-id="element-3976">
                  {t('pages.BillFinancingWizard.maximum')}{' '}
                  {bill.amount.toLocaleString('en-US')}
                </div>
              </div>
              <div data-id="element-3977">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3978">
                  {t('pages.BillFinancingWizard.requested_term_days')}
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3979">
                  <option value="15" data-id="element-3980">
                    {t('pages.BillFinancingWizard.15_days')}
                  </option>
                  <option value="30" data-id="element-3981">
                    {t('pages.BillFinancingWizard.30_days')}
                  </option>
                  <option value="45" data-id="element-3982">
                    {t('pages.BillFinancingWizard.45_days')}
                  </option>
                  <option value="60" data-id="element-3983">
                    {t('pages.BillFinancingWizard.60_days')}
                  </option>
                  <option value="90" data-id="element-3984">
                    {t('pages.BillFinancingWizard.90_days')}
                  </option>
                </select>
              </div>
              <div data-id="element-3985">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3986">
                  {t('pages.BillFinancingWizard.purpose_of_financing')}
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3987">
                  <option value="" data-id="element-3988">
                    {t('pages.BillFinancingWizard.select_purpose')}
                  </option>
                  <option value="early_payment" data-id="element-3989">
                    {t('pages.BillFinancingWizard.early_payment_discount')}
                  </option>
                  <option value="cash_flow" data-id="element-3990">
                    {t('pages.BillFinancingWizard.cash_flow_management')}
                  </option>
                  <option value="supplier_relationship" data-id="element-3991">
                    {t('pages.BillFinancingWizard.supplier_relationship')}
                  </option>
                  <option value="other" data-id="element-3992">
                    {t('pages.BillFinancingWizard.other')}
                  </option>
                </select>
              </div>
              <div data-id="element-3993">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3994">
                  {t('pages.BillFinancingWizard.additional_notes')}
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder={t('pages.BillFinancingWizard.attrs.any_additional_information')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3995" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3996">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3997">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3998">
                {t('pages.BillFinancingWizard.review_request')}
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-3999">
            <div data-id="element-4000">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4001">
                {t('pages.BillFinancingWizard.review_financing_request')}
              </h2>
              <p className="text-gray-600" data-id="element-4002">
                {t('pages.BillFinancingWizard.please_review_your_financing_request_before')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-4003">
              <div data-id="element-4004">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4005">
                  {t('pages.BillFinancingWizard.bill_information_2')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4006">
                  <div data-id="element-4007">
                    <div className="text-sm text-gray-500" data-id="element-4008">
                      {t('pages.BillFinancingWizard.bill_number')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4009">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-4010">
                    <div className="text-sm text-gray-500" data-id="element-4011">
                      {t('pages.BillFinancingWizard.supplier')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4012">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-4013">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4014">
                  {t('pages.BillFinancingWizard.financing_details')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4015">
                  <div data-id="element-4016">
                    <div className="text-sm text-gray-500" data-id="element-4017">
                      {t('pages.BillFinancingWizard.bank')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4018">
                      {t('pages.BillFinancingWizard.pomelo')}
                    </div>
                  </div>
                  <div data-id="element-4019">
                    <div className="text-sm text-gray-500" data-id="element-4020">
                      {t('pages.BillFinancingWizard.package')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4021">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-4022">
                    <div className="text-sm text-gray-500" data-id="element-4023">
                      {t('pages.BillFinancingWizard.interest_rate')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4024">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-4025">
                    <div className="text-sm text-gray-500" data-id="element-4026">
                      {t('pages.BillFinancingWizard.processing_time')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4027">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-4028">
                    <div className="text-sm text-gray-500" data-id="element-4029">
                      {t('pages.BillFinancingWizard.financing_amount')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4030">
                      {t('pages.BillFinancingWizard.rs')}{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-US')}
                    </div>
                  </div>
                  <div data-id="element-4031">
                    <div className="text-sm text-gray-500" data-id="element-4032">
                      {t('pages.BillFinancingWizard.term')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4033">
                      {formData.requestedTerm}{' '}
                      {t('pages.BillFinancingWizard.days')}
                    </div>
                  </div>
                  <div data-id="element-4034">
                    <div className="text-sm text-gray-500" data-id="element-4035">
                      {t('pages.BillFinancingWizard.purpose')}
                    </div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-4036">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-4037">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4038">
                    {t('pages.BillFinancingWizard.additional_notes_2')}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-4039">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4040">
              <div className="flex items-start gap-3" data-id="element-4041">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4042" />
                <div className="text-sm text-blue-800" data-id="element-4043">
                  {t('pages.BillFinancingWizard.by_submitting_this_request_you_agree')}{' '}
                  {selectedPackage?.name}
                  {t('pages.BillFinancingWizard.the_request_will_be_processed_within')}{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4044">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4045">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-4046">
                {t('pages.BillFinancingWizard.submit_financing_request')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}